import { useMemo, useState } from 'react';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useTags } from '@/hooks/tags/useTags';
import { createFileRoute, useRouter, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { cn, formatLabel } from '@/lib/utils';
import { tradeDotClass } from '@/lib/scheduler/trade-colors';
import { toast } from 'sonner';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Bot,
  Bell,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  Copy,
  FileSignature,
  Filter,
  LayoutGrid,
  List,
  MoreHorizontal,
  Pencil,
  Plus,
  ReceiptText,
  Search,
  Settings,
  ShieldCheck,
  Trash2,
  Users,
  Wrench,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { StatusChip } from '@/components/molecules/StatusChip';
import { JobTypeAppearanceTile } from '@/components/molecules/JobTypeAppearanceTile';
import {
  SettingsDataGrid,
  type SettingsDataGridColumn,
  type SettingsDataGridSortDir,
} from '@/components/organisms';
import {
  JobTypeFormContent,
  type ChecklistLinkDraft,
  type JobTypeFormValues,
} from '@/components/organisms/settings-job-types/JobTypeFormDrawer';
import {
  checklistLinksForJobType,
  type JobTypeChecklistDefinition,
  jobTypeFormValuesToDto,
  normalizeJobTypeRecurrenceFrequency,
  readIrisListingFields,
  readIrisSurveyQuestions,
  readServiceCallFeePolicyField,
  requiredSkillsForWrite,
  specificRequiredSkills,
} from '@/components/organisms/settings-job-types/jobTypeFormUtils';
import {
  existingLineItemToEditable,
  type EditableLineItem,
} from '@/routes/_dashboard/dashboard/estimates/_shared';
import { DetailPageLayout } from '@/components/layouts';
import { SettingsPageHeader } from '@/components/layouts/SettingsPageHeader';
import { Fullbleed } from '@/contexts/fullbleed';
import { resolveJobTypeBookingType } from '@/lib/jobs/jobFormModel';
import { readTermsSelection } from '@/lib/terms';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import { useCreateJobType } from '@/hooks/settings/useCreateJobType';
import { useUpdateJobType } from '@/hooks/settings/useUpdateJobType';
import { useDeleteJobType } from '@/hooks/settings/useDeleteJobType';
import { useJobPipelines } from '@/hooks/pipelines/useJobPipelines';
import {
  useDeleteFormTemplateAssignment,
  useFormTemplates,
  useUpsertFormTemplateAssignment,
} from '@/hooks/forms-engine/useFormTemplates';
import { apiGet, apiPatch, operationsClient } from '@/lib/api';
import { schedulerEndpoints, settingsEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys, settingsKeys } from '@/lib/queryKeys';
import type { ServiceType } from '@/types/scheduler';
import type { JobType, JobPriority, RequiredSkillDraft } from '@/types/settings';
import type { FormTemplate } from '@/types/formsEngine';

export const Route = createFileRoute('/_dashboard/dashboard/settings/jobtypes')({
  component: JobTypesPage,
});

const PAGE_SIZE = 25;
const JOB_TYPE_NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/jobtypes\/new\/?$/;
const JOB_TYPE_EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/jobtypes\/([^/]+)\/edit\/?$/;
const JOB_TYPE_DETAIL_ROUTE_PATTERN = /^\/dashboard\/settings\/jobtypes\/([^/]+)\/?$/;

type JobTypeColumn =
  | 'jobType'
  | 'shortCode'
  | 'duration'
  | 'priority'
  | 'price'
  | 'skills'
  | 'checklists'
  | 'status'
  | 'updatedAt';
type JobTypeStatusFilter = 'all' | 'active' | 'inactive';
type JobTypePriorityFilter = 'all' | JobPriority;
type JobTypeViewMode = 'library' | 'table';
type JobTypeTableRow =
  | { kind: 'jobType'; id: string; jobType: JobType }
  | { kind: 'detail'; id: string; jobType: JobType };

const JOB_TYPE_COLUMNS: SettingsDataGridColumn<JobTypeColumn>[] = [
  { key: 'jobType', label: 'Job Type', sortable: true, width: 220, flex: 2 },
  {
    key: 'shortCode',
    label: 'Code',
    sortable: true,
    align: 'center',
    width: 80,
  },
  {
    key: 'duration',
    label: 'Duration',
    sortable: true,
    align: 'center',
    width: 90,
  },
  {
    key: 'priority',
    label: 'Priority',
    sortable: true,
    align: 'center',
    width: 90,
  },
  { key: 'price', label: 'Price', sortable: true, align: 'right', width: 100 },
  {
    key: 'skills',
    label: 'Skills',
    sortable: true,
    align: 'center',
    width: 80,
  },
  {
    key: 'checklists',
    label: 'Forms',
    sortable: true,
    align: 'center',
    width: 100,
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    align: 'center',
    width: 90,
  },
  {
    key: 'updatedAt',
    label: 'Updated',
    sortable: true,
    defaultVisible: false,
    width: 160,
  },
];

const PRIORITY_OPTIONS: { value: JobTypePriorityFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  { value: 'EMERGENCY', label: 'Emergency' },
];

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatPrice(value: number | null | undefined) {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value));
}

/**
 * A job type's price is now defined by its pricebook line items. Sum the
 * selected rows' base price × quantity for the list/detail summary.
 */
function jobTypeLineItemSummary(jobType: JobType): { count: number; total: number } {
  const rows = jobType.lineItems ?? [];
  let total = 0;
  let count = 0;
  for (const raw of rows) {
    if (raw.isSelected === false) continue;
    count += 1;
    // Trust the persisted, engine-computed lineTotal when present (covers T&M /
    // cost-plus / discounted rows) — even $0 for a fully discounted line. Only
    // reconstruct basePrice * quantity for legacy rows with no stored total.
    if (raw.lineTotal != null) {
      const lineTotal = Number(raw.lineTotal);
      if (Number.isFinite(lineTotal)) {
        total += lineTotal;
        continue;
      }
    }
    const basePrice = Number(raw.basePrice ?? 0);
    const quantity = Number(raw.quantity ?? 1);
    if (Number.isFinite(basePrice) && Number.isFinite(quantity)) {
      total += basePrice * quantity;
    }
  }
  return { count, total };
}

function checklistLinkSummaryForJobType(
  jobTypeId: string,
  definitions: JobTypeChecklistDefinition[],
) {
  return definitions
    .flatMap((definition) => {
      const link = definition.jobTypeLinks?.find((item) => item.jobTypeId === jobTypeId);
      if (!link) return [];
      return [{ definition, link }];
    })
    .sort((a, b) => (a.link.sortOrder ?? 0) - (b.link.sortOrder ?? 0));
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours} h` : `${hours} h ${remainder} min`;
}

function selectedLineItems(jobType: JobType) {
  return (jobType.lineItems ?? []).filter((item) => item.isSelected !== false);
}

function lineItemName(item: Record<string, unknown>) {
  return typeof item.name === 'string' && item.name.trim() ? item.name : 'Pricebook item';
}

function lineItemQuantity(item: Record<string, unknown>) {
  const quantity = Number(item.quantity ?? 1);
  return Number.isFinite(quantity) ? quantity : 1;
}

function lineItemTotal(item: Record<string, unknown>) {
  const persisted = Number(item.lineTotal);
  if (item.lineTotal != null && Number.isFinite(persisted)) return persisted;
  const basePrice = Number(item.basePrice ?? 0);
  return Number.isFinite(basePrice) ? basePrice * lineItemQuantity(item) : 0;
}

function recurrenceLabel(jobType: JobType) {
  if (jobType.recurrenceFrequency === 'NONE') return 'One-off';
  const unit = formatLabel(jobType.recurrenceFrequency).toLowerCase();
  return `Every ${jobType.recurrenceInterval} ${unit}${jobType.recurrenceInterval === 1 ? '' : 's'}`;
}

function jobTypeGroup(jobType: JobType) {
  return jobType.billingCategory?.trim() || 'Other templates';
}

function getSortValue(
  column: JobTypeColumn,
  jobType: JobType,
  checklistDefinitions: JobTypeChecklistDefinition[],
) {
  switch (column) {
    case 'jobType':
      return jobType.name.toLowerCase();
    case 'shortCode':
      return jobType.shortCode.toLowerCase();
    case 'duration':
      return jobType.defaultDuration;
    case 'priority':
      return jobType.defaultPriority.toLowerCase();
    case 'price':
      return jobTypeLineItemSummary(jobType).total;
    case 'skills':
      return specificRequiredSkills(jobType.requiredSkills).length;
    case 'checklists':
      return checklistLinkSummaryForJobType(jobType.id, checklistDefinitions).length;
    case 'status':
      return jobType.isActive ? 'active' : 'inactive';
    case 'updatedAt':
      return Date.parse(jobType.updatedAt) || 0;
  }
}

function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

export function JobTypesPage() {
  const router = useRouter();
  const { location } = useRouterState();
  const queryClient = useQueryClient();
  const { data: jobTypes = [], isLoading: jobTypesLoading } = useJobTypes();
  // Job-scope tag catalog — used to scrub stale stamped ids from a duplicate
  // before they hit the create endpoint's strict tag validation.
  const jobScopeTagsQuery = useTags('job');
  const { data: assignableTemplates = [], isLoading: checklistsLoading } = useFormTemplates({
    status: 'ACTIVE',
  });
  const checklistTemplates = useMemo(
    () => assignableTemplates.filter((template) => template.surface === 'CHECKLIST'),
    [assignableTemplates],
  );
  const { data: skills = [] } = useApiQuery<ServiceType[]>({
    queryKey: schedulerKeys.serviceTypes(),
    queryFn: () => apiGet<ServiceType[]>(operationsClient, schedulerEndpoints.serviceTypes()),
    showErrorToast: false,
  });
  const createJobType = useCreateJobType();
  const updateJobType = useUpdateJobType();
  const deleteJobType = useDeleteJobType();
  const archiveJobType = useApiMutation<JobType, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      apiPatch<JobType, { isActive: boolean }>(operationsClient, settingsEndpoints.jobType(id), {
        isActive,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.jobTypes() });
      toast.success(
        variables.isActive
          ? 'Job type restored.'
          : 'Job type archived — existing jobs keep it; it no longer appears when creating new jobs.',
      );
    },
  });
  const upsertChecklistAssignment = useUpsertFormTemplateAssignment();
  const deleteChecklistAssignment = useDeleteFormTemplateAssignment();

  const assignableFormDefinitions = useMemo<JobTypeChecklistDefinition[]>(
    () =>
      assignableTemplates
        .filter((template) => template.surface === 'CHECKLIST' || template.surface === 'VISIT_FORM')
        .map((template: FormTemplate) => ({
          id: template.id,
          name: template.name,
          description: template.description,
          version: template.version,
          status: template.status,
          isDeleted: false,
          surface: template.surface,
          publicFillable: template.publicFillable,
          // Required wherever it is already linked as required — the same
          // reading the forms library shows.
          blocksCompletion: template.assignments.some((assignment) => assignment.blocksCompletion),
          jobTypeLinks: template.assignments
            .filter(
              (assignment) => assignment.targetType === 'JOB_TYPE' && assignment.targetId != null,
            )
            .map((assignment) => ({
              id: assignment.id,
              checklistDefinitionId: template.id,
              jobTypeId: assignment.targetId!,
              blocksCompletion: assignment.blocksCompletion,
              sortOrder: assignment.sortOrder,
              audience: assignment.audience,
              instantiationOverride: assignment.instantiationOverride,
              autoAttach: assignment.autoAttach ?? null,
            })),
        })),
    [assignableTemplates],
  );
  const createOpen = JOB_TYPE_NEW_ROUTE_PATTERN.test(location.pathname);
  const editJobTypeId = location.pathname.match(JOB_TYPE_EDIT_ROUTE_PATTERN)?.[1];
  const decodedEditJobTypeId = editJobTypeId ? decodeURIComponent(editJobTypeId) : undefined;
  const editJobType = useMemo(
    () =>
      decodedEditJobTypeId
        ? (jobTypes.find((jobType) => jobType.id === decodedEditJobTypeId) ?? null)
        : null,
    [decodedEditJobTypeId, jobTypes],
  );

  const rawDetailId = location.pathname.match(JOB_TYPE_DETAIL_ROUTE_PATTERN)?.[1];
  const detailJobTypeId =
    rawDetailId && rawDetailId !== 'new' ? decodeURIComponent(rawDetailId) : undefined;
  const [viewMode, setViewMode] = useState<JobTypeViewMode>('library');
  const [selectedJobTypeId, setSelectedJobTypeId] = useState<string | null>(
    detailJobTypeId ?? null,
  );
  const [mobileDetailOpen, setMobileDetailOpen] = useState(Boolean(detailJobTypeId));
  const [expandedTableJobTypeId, setExpandedTableJobTypeId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobTypeStatusFilter>('active');
  const [priorityFilter, setPriorityFilter] = useState<JobTypePriorityFilter>('all');
  const [sortKey, setSortKey] = useState<JobTypeColumn | null>('jobType');
  const [sortDir, setSortDir] = useState<SettingsDataGridSortDir>('asc');
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<JobType | null>(null);
  const [duplicatingJobTypeId, setDuplicatingJobTypeId] = useState<string | null>(null);

  const filteredJobTypes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return jobTypes.filter((jobType) => {
      const linkedChecklists = checklistLinkSummaryForJobType(
        jobType.id,
        assignableFormDefinitions,
      );
      const matchesSearch =
        !q ||
        [
          jobType.name,
          jobType.shortCode,
          jobType.description ?? '',
          ...(jobType.customerPhrases ?? []),
          jobType.defaultPriority,
          ...specificRequiredSkills(jobType.requiredSkills).map((skill) => skill.name),
          ...linkedChecklists.map(({ definition }) => definition.name),
        ]
          .join(' ')
          .toLowerCase()
          .includes(q);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && jobType.isActive) ||
        (statusFilter === 'inactive' && !jobType.isActive);
      const matchesPriority =
        priorityFilter === 'all' || jobType.defaultPriority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [assignableFormDefinitions, jobTypes, priorityFilter, search, statusFilter]);

  const sortedJobTypes = useMemo(() => {
    if (!sortKey) return filteredJobTypes;
    return [...filteredJobTypes].sort((a, b) => {
      const result = compareValues(
        getSortValue(sortKey, a, assignableFormDefinitions),
        getSortValue(sortKey, b, assignableFormDefinitions),
      );
      return sortDir === 'asc' ? result : -result;
    });
  }, [assignableFormDefinitions, filteredJobTypes, sortDir, sortKey]);

  const activeFilterCount =
    (statusFilter !== 'active' ? 1 : 0) + (priorityFilter !== 'all' ? 1 : 0);
  const totalPages = Math.max(1, Math.ceil(sortedJobTypes.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageJobTypes = sortedJobTypes.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const tableRows = useMemo<JobTypeTableRow[]>(
    () =>
      pageJobTypes.flatMap((jobType) => [
        { kind: 'jobType' as const, id: jobType.id, jobType },
        ...(expandedTableJobTypeId === jobType.id
          ? [{ kind: 'detail' as const, id: `${jobType.id}:detail`, jobType }]
          : []),
      ]),
    [expandedTableJobTypeId, pageJobTypes],
  );
  const selectedJobType =
    sortedJobTypes.find((jobType) => jobType.id === selectedJobTypeId) ?? sortedJobTypes[0] ?? null;
  const isDrawerPending =
    createJobType.isPending ||
    updateJobType.isPending ||
    upsertChecklistAssignment.isPending ||
    deleteChecklistAssignment.isPending;

  function resetFilters() {
    setSearch('');
    setStatusFilter('active');
    setPriorityFilter('all');
    setPage(1);
  }

  function handleSort(column: SettingsDataGridColumn<JobTypeColumn>, dir: SettingsDataGridSortDir) {
    if (!column.sortable) return;
    setSortKey(column.key);
    setSortDir(dir);
    setPage(1);
  }

  function openCreateDrawer() {
    void router.navigate({ to: '/dashboard/settings/jobtypes/new' });
  }

  function openEditDrawer(jobTypeId: string) {
    void router.navigate({
      to: '/dashboard/settings/jobtypes/$id/edit',
      params: { id: jobTypeId },
    });
  }

  function closeDrawer() {
    void router.navigate({ to: '/dashboard/settings/jobtypes' });
  }

  function openDetail(jobTypeId: string) {
    setSelectedJobTypeId(jobTypeId);
    setMobileDetailOpen(true);
  }

  async function syncChecklistLinks(
    jobTypeId: string,
    initialLinks: ChecklistLinkDraft[],
    selectedLinks: ChecklistLinkDraft[],
  ) {
    const before = new Map(initialLinks.map((link) => [link.checklistDefinitionId, link]));
    const after = new Map(selectedLinks.map((link) => [link.checklistDefinitionId, link]));

    await Promise.all([
      ...selectedLinks.map((link) => {
        const original = before.get(link.checklistDefinitionId);
        if (!original) {
          return upsertChecklistAssignment.mutateAsync({
            templateId: link.checklistDefinitionId,
            targetType: 'JOB_TYPE',
            targetId: jobTypeId,
            audience: link.audience ?? 'TECHNICIAN',
            instantiationOverride: link.instantiationOverride,
            autoAttach: link.autoAttach ?? null,
            blocksCompletion: link.blocksCompletion,
            sortOrder: link.sortOrder,
          });
        }

        if (
          original.blocksCompletion !== link.blocksCompletion ||
          original.sortOrder !== link.sortOrder ||
          original.audience !== link.audience ||
          original.autoAttach !== link.autoAttach
        ) {
          return upsertChecklistAssignment.mutateAsync({
            templateId: link.checklistDefinitionId,
            targetType: 'JOB_TYPE',
            targetId: jobTypeId,
            audience: link.audience ?? 'TECHNICIAN',
            instantiationOverride: link.instantiationOverride,
            autoAttach: link.autoAttach ?? null,
            blocksCompletion: link.blocksCompletion,
            sortOrder: link.sortOrder,
          });
        }

        return Promise.resolve();
      }),
      ...initialLinks
        .filter((link) => !after.has(link.checklistDefinitionId))
        .map((link) => {
          const assignmentId = assignableFormDefinitions
            .find((definition) => definition.id === link.checklistDefinitionId)
            ?.jobTypeLinks?.find((candidate) => candidate.jobTypeId === jobTypeId)?.id;
          return assignmentId
            ? deleteChecklistAssignment.mutateAsync({
                templateId: link.checklistDefinitionId,
                assignmentId,
              })
            : Promise.resolve();
        }),
    ]);
  }

  async function handleCreate(
    values: JobTypeFormValues,
    requiredSkills: RequiredSkillDraft[],
    checklistLinks: ChecklistLinkDraft[],
    lineItems: EditableLineItem[],
  ) {
    const created = await createJobType.mutateAsync(
      jobTypeFormValuesToDto(values, requiredSkills, lineItems),
    );
    if (checklistLinks.length > 0) {
      await syncChecklistLinks(created.id, [], checklistLinks);
    }
    closeDrawer();
  }

  async function handleUpdate(
    jobTypeId: string,
    values: JobTypeFormValues,
    requiredSkills: RequiredSkillDraft[],
    checklistLinks: ChecklistLinkDraft[],
    lineItems: EditableLineItem[],
    updateDirtyGroups: { serviceCall: boolean; scheduling: boolean },
  ) {
    const previousLinks = checklistLinksForJobType(jobTypeId, assignableFormDefinitions);
    const persistedJobType = jobTypes.find((jobType) => jobType.id === jobTypeId);
    await updateJobType.mutateAsync({
      id: jobTypeId,
      dto: jobTypeFormValuesToDto(values, requiredSkills, lineItems, {
        preserveUnspecifiedBusinessUnit: true,
        persistedAuthorizationConfig: persistedJobType?.authorizationConfig,
        persistedSignoffConfig: persistedJobType?.signoffConfig,
        // While Iris is deactivated the Iris-only content is inert: omit it so
        // the server preserves the stored row untouched (stale legacy content
        // must neither block Save nor be re-normalized in passing).
        preserveInertIrisContent: true,
        // Decision C tolerance: serialize the service-call/scheduling groups
        // only when this save touched them — the backend re-validates any
        // present field, and a legacy enabled+recurring row must stay
        // editable for unrelated changes.
        updateDirtyGroups,
      }),
    });
    await syncChecklistLinks(jobTypeId, previousLinks, checklistLinks);
    closeDrawer();
  }

  async function handleDuplicate(jobType: JobType) {
    setDuplicatingJobTypeId(jobType.id);
    try {
      const existingNames = new Set(jobTypes.map(({ name }) => name.toLowerCase()));
      const existingCodes = new Set(jobTypes.map(({ shortCode }) => shortCode.toUpperCase()));
      let copyNumber = 1;
      let name = `${jobType.name} Copy`;
      while (existingNames.has(name.toLowerCase())) {
        copyNumber += 1;
        name = `${jobType.name} Copy ${copyNumber}`;
      }

      const codeBase =
        jobType.shortCode
          .replace(/[^A-Z0-9]/gi, '')
          .toUpperCase()
          .slice(0, 7) || 'JT';
      let shortCode = `${codeBase}CP`.slice(0, 10);
      let codeNumber = 1;
      while (existingCodes.has(shortCode)) {
        codeNumber += 1;
        const suffix = `CP${codeNumber}`;
        shortCode = `${codeBase.slice(0, 10 - suffix.length)}${suffix}`;
      }

      const schedulingAvailability = resolveJobTypeBookingType(jobType);
      const values: JobTypeFormValues = {
        name,
        shortCode,
        color: jobType.color,
        icon: jobType.icon ?? '',
        pipelineId: jobType.pipelineId ?? null,
        businessUnitIds: jobType.businessUnitIds ?? [],
        // A duplicate keeps stamping the same tags on its jobs.
        // Duplicate goes through strict CREATE validation, which — unlike the
        // update path — has no stored set to grandfather: a stale stamped id
        // (deleted through a path the cleanup missed) would 400 the whole
        // duplicate with nothing visible in the picker. Scrub against the
        // loaded catalog; while the catalog is still loading, pass through
        // unfiltered rather than silently dropping every tag.
        tagIds: jobScopeTagsQuery.data
          ? (jobType.tagIds ?? []).filter((id) =>
              jobScopeTagsQuery.data.some((tag) => tag.id === id),
            )
          : (jobType.tagIds ?? []),
        description: jobType.description ?? '',
        customerPhrases: (jobType.customerPhrases ?? []).join('\n'),
        // SPEC §4.1 — stored irisMode reads into the Iris activation switch
        // (legacy SERVICE_CALL rows read back as activated); the service-call
        // capability carries into the copy.
        ...readIrisListingFields(jobType),
        // NOT-560 — dual-read: older rows without the flag derive it from the
        // fallback markers (a diagnostic fallback is always service-call enabled).
        isDiagnostic:
          jobType.scopeMode === 'DIAGNOSTIC' ||
          jobType.serviceCallEnabled === true ||
          jobType.irisMode === 'SERVICE_CALL' ||
          jobType.isServiceCallFallback,
        serviceCallFeePolicy: readServiceCallFeePolicyField(jobType.serviceCallFeePolicy),
        visitDepositEnabled: jobType.visitDepositEnabled ?? false,
        visitDepositType:
          jobType.visitDepositType === 'PERCENT' ? ('PERCENT' as const) : ('FLAT' as const),
        visitDepositValue: Number(jobType.visitDepositValue ?? 0),
        irisSurveyQuestions: readIrisSurveyQuestions(jobType.irisSurveyQuestions),
        irisSurveyTiming:
          jobType.irisSurveyTiming === 'AFTER_SLOT'
            ? ('AFTER_SLOT' as const)
            : ('BEFORE_SLOTS' as const),
        irisAskEquipment: jobType.irisAskEquipment ?? false,
        irisCustomerDescription: jobType.irisProfile?.customerDescription ?? '',
        irisMatchingExclusions: (jobType.irisProfile?.matchingExclusions ?? []).join('\n'),
        irisIncludedWork: (jobType.irisProfile?.includedWork ?? []).join('\n'),
        irisExcludedWork: (jobType.irisProfile?.excludedWork ?? []).join('\n'),
        irisFaqs: (jobType.irisProfile?.faqs ?? [])
          .map((faq) => `${faq.question} | ${faq.answer}`)
          .join('\n'),
        irisWarranty: jobType.irisProfile?.warranty ?? '',
        irisAdditionalGuidance: jobType.irisProfile?.additionalGuidance ?? '',
        onDutySchedulingEnabled: jobType.onDutySchedulingEnabled ?? false,
        requiresParts: jobType.requiresParts ?? false,
        defaultDuration: jobType.defaultDuration,
        defaultPriority: jobType.defaultPriority,
        minCrewSize: jobType.minCrewSize,
        recurrenceFrequency: normalizeJobTypeRecurrenceFrequency(
          jobType.recurrenceFrequency,
          schedulingAvailability === 'RECURRING',
        ),
        recurrenceInterval: jobType.recurrenceInterval,
        schedulingAvailability,
        oneOffPatternMode: jobType.schedulingDefaults?.oneOff?.patternMode ?? 'PRESET',
        oneOffScheduleMode: jobType.schedulingDefaults?.oneOff?.scheduleMode ?? 'SCHEDULE_NOW',
        oneOffVisitShape: jobType.schedulingDefaults?.oneOff?.visitShape ?? 'TIMED',
        oneOffPreferredTimeWindow: jobType.schedulingDefaults?.oneOff?.preferredTimeWindow ?? 'ANY',
        oneOffSpecificStartTime: jobType.schedulingDefaults?.oneOff?.specificStartTime ?? null,
        oneOffConsecutiveDays: jobType.schedulingDefaults?.oneOff?.consecutiveDays ?? 2,
        oneOffUrgency: jobType.schedulingDefaults?.oneOff?.urgency ?? 'STANDARD',
        recurringPatternMode: jobType.schedulingDefaults?.recurring?.patternMode ?? 'FIXED',
        recurringVisitsPerCycle: jobType.schedulingDefaults?.recurring?.visitsPerCycle ?? 1,
        recurringDayConstraintMode:
          jobType.schedulingDefaults?.recurring?.dayConstraintMode ??
          ((jobType.schedulingDefaults?.recurring?.allowedWeekDays?.length ?? 0) > 0
            ? 'INCLUDE'
            : 'ANY'),
        recurringAllowedWeekDays: jobType.schedulingDefaults?.recurring?.allowedWeekDays ?? [],
        recurringPlacement: jobType.schedulingDefaults?.recurring?.placement ?? 'SCHEDULE_NOW',
        recurringWeekDays: jobType.schedulingDefaults?.recurring?.weekDays ?? [],
        recurringMonthMode: jobType.schedulingDefaults?.recurring?.monthMode ?? 'DAY_OF_MONTH',
        recurringMonthDay: jobType.schedulingDefaults?.recurring?.monthDay ?? 1,
        recurringMonthLastDay: jobType.schedulingDefaults?.recurring?.monthLastDay ?? false,
        recurringMonthDayOfWeek: jobType.schedulingDefaults?.recurring?.monthDayOfWeek ?? 1,
        recurringMonthDayOfWeekOccurrence:
          jobType.schedulingDefaults?.recurring?.monthDayOfWeekOccurrence ?? 1,
        recurringAnnualMonth: jobType.schedulingDefaults?.recurring?.annualMonth ?? 1,
        recurringAnnualDay: jobType.schedulingDefaults?.recurring?.annualDay ?? 1,
        recurringTimeMode: jobType.schedulingDefaults?.recurring?.timeMode ?? 'SAME',
        recurringTechnicianMode:
          jobType.schedulingDefaults?.recurring?.technicianMode ?? 'PER_VISIT',
        recurringVisitStartTime: jobType.schedulingDefaults?.recurring?.visitStartTime ?? '09:00',
        recurringVisitEndTime: jobType.schedulingDefaults?.recurring?.visitEndTime ?? '10:00',
        authRequired: jobType.authorizationConfig?.required ?? false,
        authMethod: 'SIGNATURE',
        signoffRequired: jobType.signoffConfig?.required ?? false,
        signoffMethod: 'SIGNATURE',
        cancellationWindowHours: jobType.cancellationWindowHours ?? '',
        cancellationFeeType: jobType.cancellationFeeType ?? 'NONE',
        cancellationFeeAmount: jobType.cancellationFeeAmount ?? '',
        depositRefundable: jobType.depositRefundable ?? true,
        customerRescheduleMode: jobType.customerRescheduleMode ?? 'REQUEST',
        rescheduleCutoffHours: jobType.rescheduleCutoffHours ?? 24,
        maxCustomerReschedules: jobType.maxCustomerReschedules ?? 1,
        defaultStageId: jobType.defaultStageId ?? null,
        schedulingNotificationPolicy: jobType.schedulingNotificationPolicy ?? 'ASK',
        confirmationMethod: jobType.confirmationMethod ?? null,
        invoicingMethod: jobType.invoicingMethod ?? null,
        invoicingSchedule: jobType.invoicingSchedule ?? null,
        autoInvoice: jobType.autoInvoice ?? null,
        autoCharge: jobType.autoCharge ?? null,
        reviewRequestDefault: jobType.reviewRequestDefault ?? null,
        weatherPolicy: jobType.weatherPolicy ?? null,
        // A duplicate inherits the source type's T&C selection.
        ...readTermsSelection(jobType),
      };
      // Same rule the editor writes by: a duplicate of an "any technician"
      // type keeps the general skill, a duplicate of a skilled one never
      // carries it along.
      const requiredSkills: RequiredSkillDraft[] = requiredSkillsForWrite(
        jobType.requiredSkills,
      ).map((skill) => ({
        serviceTypeId: skill.id,
      }));
      const lineItems = (jobType.lineItems ?? []).map((raw) =>
        existingLineItemToEditable(raw as Record<string, unknown>),
      );
      const checklistLinks = checklistLinksForJobType(jobType.id, assignableFormDefinitions);
      const created = await createJobType.mutateAsync(
        jobTypeFormValuesToDto(values, requiredSkills, lineItems, {
          persistedAuthorizationConfig: jobType.authorizationConfig,
          persistedSignoffConfig: jobType.signoffConfig,
          // A deactivated source's Iris content is inert (and may carry stale
          // legacy values that fail server caps): the copy starts with an
          // empty Iris config instead of 400-ing the duplicate. Activated and
          // fallback sources copy their content in full, as before.
          preserveInertIrisContent: true,
        }),
      );
      if (checklistLinks.length > 0) {
        await syncChecklistLinks(created.id, [], checklistLinks);
      }
      setSelectedJobTypeId(created.id);
      openEditDrawer(created.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to duplicate job type');
    } finally {
      setDuplicatingJobTypeId(null);
    }
  }

  function renderCell(key: JobTypeColumn, jobType: JobType) {
    const linkedChecklists = checklistLinkSummaryForJobType(jobType.id, assignableFormDefinitions);
    const blockingCount = linkedChecklists.filter(({ link }) => link.blocksCompletion).length;

    switch (key) {
      case 'jobType':
        return (
          <div className="flex min-w-0 items-center gap-3">
            <JobTypeAppearanceTile color={jobType.color} icon={jobType.icon} size="md" />
            <div className="min-w-0">
              {/* NOT-468 — the inactive state lives in the dedicated Status
                  column; a second badge here duplicated it and broke the row
                  layout, clipping the description. Name + description only. */}
              <p className="truncate text-sm font-medium">{jobType.name}</p>
              {jobType.description ? (
                <p className="truncate text-xs text-muted-foreground">{jobType.description}</p>
              ) : null}
            </div>
          </div>
        );
      case 'shortCode':
        return (
          <Badge variant="outline" className="font-mono text-xs">
            {jobType.shortCode}
          </Badge>
        );
      case 'duration':
        return (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Clock className="h-3 w-3 text-muted-foreground" />
            {jobType.defaultDuration}m
          </span>
        );
      case 'priority':
        return <Badge variant="secondary">{formatLabel(jobType.defaultPriority)}</Badge>;
      case 'price': {
        const { count, total } = jobTypeLineItemSummary(jobType);
        if (count === 0) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="flex flex-col items-end leading-tight">
            <span className="tabular-nums">{formatPrice(total)}</span>
            <span className="text-[10px] text-muted-foreground">
              {count} item{count === 1 ? '' : 's'}
            </span>
          </span>
        );
      }
      case 'skills':
        return (
          <span className="inline-flex items-center gap-1 tabular-nums">
            <Wrench className="h-3 w-3 text-muted-foreground" />
            {specificRequiredSkills(jobType.requiredSkills).length}
          </span>
        );
      case 'checklists':
        return linkedChecklists.length > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 tabular-nums">
              <ClipboardList className="h-3 w-3 text-muted-foreground" />
              {linkedChecklists.length}
            </span>
            {blockingCount > 0 ? (
              <Badge variant="outline" className="text-[10px]">
                {blockingCount} blocking
              </Badge>
            ) : null}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      case 'status':
        return (
          <StatusChip
            status={jobType.isActive ? 'ACTIVE' : 'INACTIVE'}
            label={statusLabel(jobType)}
            variant="table"
          />
        );
      case 'updatedAt':
        return <span className="text-muted-foreground">{formatDate(jobType.updatedAt)}</span>;
    }
  }

  const groupedJobTypes = Object.entries(
    sortedJobTypes.reduce<Record<string, JobType[]>>((groups, jobType) => {
      const group = jobTypeGroup(jobType);
      groups[group] = [...(groups[group] ?? []), jobType];
      return groups;
    }, {}),
  );
  const archivedCount = jobTypes.filter((jobType) => !jobType.isActive).length;
  const archivedJobTypes = jobTypes
    .filter((jobType) => !jobType.isActive)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (createOpen || decodedEditJobTypeId) {
    return (
      <>
        <Fullbleed />
        {jobTypesLoading || checklistsLoading ? (
          <div className="grid min-h-screen place-items-center bg-[#f4f4f1] text-sm text-muted-foreground">
            Loading job type...
          </div>
        ) : (
          <JobTypeFormContent
            // Keyed per NAVIGATION ENTRY, not per mode: with a constant
            // 'create' key, an instance that survived from a previous create
            // (or any residual reconciliation path) kept its line-item state —
            // observed live: a brand-new job type opened pre-filled with the
            // previous type's $100 visit line. location.state.key changes on
            // every navigation, so each arrival at /new provably remounts.
            key={
              createOpen ? `create-${location.state?.key ?? location.href}` : decodedEditJobTypeId
            }
            layout="page"
            mode={createOpen ? 'create' : 'edit'}
            jobType={createOpen ? null : editJobType}
            skills={skills}
            checklistDefinitions={assignableFormDefinitions}
            checklistsLoading={checklistsLoading}
            isPending={isDrawerPending}
            onClose={closeDrawer}
            onBack={closeDrawer}
            onCreate={(values, requiredSkillIds, checklistLinks, lineItems) => {
              void handleCreate(values, requiredSkillIds, checklistLinks, lineItems).catch(
                (error: unknown) =>
                  toast.error(error instanceof Error ? error.message : 'Failed to save'),
              );
            }}
            onUpdate={(jobTypeId, values, requiredSkillIds, checklistLinks, lineItems, dirty) => {
              void handleUpdate(
                jobTypeId,
                values,
                requiredSkillIds,
                checklistLinks,
                lineItems,
                dirty,
              ).catch((error: unknown) =>
                toast.error(error instanceof Error ? error.message : 'Failed to save'),
              );
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Fullbleed />
      <DetailPageLayout
        header={
          <SettingsPageHeader
            title="Job Types"
            meta={
              <div className="hidden items-center rounded-md border bg-muted/40 p-0.5 sm:flex">
                <button
                  type="button"
                  className={cn(
                    'inline-flex h-7 items-center gap-1.5 rounded px-3 text-xs font-medium',
                    viewMode === 'library'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground',
                  )}
                  onClick={() => setViewMode('library')}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  List
                </button>
                <button
                  type="button"
                  className={cn(
                    'inline-flex h-7 items-center gap-1.5 rounded px-3 text-xs font-medium',
                    viewMode === 'table'
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground',
                  )}
                  onClick={() => setViewMode('table')}
                >
                  <List className="h-3.5 w-3.5" />
                  Table
                </button>
              </div>
            }
            actions={
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8">
                      <Archive className="h-3.5 w-3.5" />
                      Archived · {archivedCount}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <div className="px-2 py-1.5">
                      <p className="text-xs font-semibold">Archived templates</p>
                      <p className="text-[11px] text-muted-foreground">
                        Restore a template to make it available again.
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    {archivedJobTypes.length > 0 ? (
                      archivedJobTypes.slice(0, 6).map((jobType) => (
                        <DropdownMenuItem
                          key={jobType.id}
                          className="gap-2 py-2"
                          disabled={archiveJobType.isPending}
                          onSelect={() =>
                            archiveJobType.mutate({
                              id: jobType.id,
                              isActive: true,
                            })
                          }
                        >
                          <JobTypeAppearanceTile
                            color={jobType.color}
                            icon={jobType.icon}
                            size="sm"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs font-medium">
                              {jobType.name}
                            </span>
                            <span className="block text-[10px] text-muted-foreground">
                              {jobType.shortCode}
                            </span>
                          </span>
                          <ArchiveRestore className="h-3.5 w-3.5 text-muted-foreground" />
                        </DropdownMenuItem>
                      ))
                    ) : (
                      <DropdownMenuItem disabled>No archived templates</DropdownMenuItem>
                    )}
                    {archivedJobTypes.length > 6 ? (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={() => {
                            setStatusFilter('inactive');
                            setViewMode('table');
                            setPage(1);
                          }}
                        >
                          View all {archivedJobTypes.length} archived templates
                        </DropdownMenuItem>
                      </>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button size="sm" onClick={openCreateDrawer}>
                  <Plus className="h-4 w-4" />
                  New template
                </Button>
              </>
            }
          />
        }
        headerClassName="min-h-[57px] px-4 py-0 sm:px-6"
        contentClassName="flex min-h-0 flex-col bg-[#f4f4f1] p-0"
      >
        <div className="mx-auto flex min-h-0 w-full max-w-[1600px] flex-1 flex-col">
          <div className="bg-background px-4 py-3 sm:hidden">
            <div className="flex items-center gap-2 sm:hidden">
              <button
                type="button"
                className={cn(
                  'flex-1 rounded-md border px-3 py-2 text-xs font-medium',
                  viewMode === 'library' && 'border-foreground bg-foreground text-background',
                )}
                onClick={() => setViewMode('library')}
              >
                List
              </button>
              <button
                type="button"
                className={cn(
                  'flex-1 rounded-md border px-3 py-2 text-xs font-medium',
                  viewMode === 'table' && 'border-foreground bg-foreground text-background',
                )}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
            </div>
          </div>

          {jobTypesLoading || checklistsLoading ? (
            <div className="grid flex-1 place-items-center text-sm text-muted-foreground">
              Loading job types...
            </div>
          ) : sortedJobTypes.length === 0 ? (
            <div className="grid flex-1 place-items-center px-6 text-center">
              <div>
                <Wrench className="mx-auto h-6 w-6 text-muted-foreground" />
                <p className="mt-2 text-sm font-medium">
                  {jobTypes.length === 0 ? 'No job types yet' : 'No matching job types found'}
                </p>
              </div>
            </div>
          ) : viewMode === 'library' ? (
            <div className="m-4 grid min-h-0 flex-1 overflow-hidden rounded-xl border bg-background lg:grid-cols-[390px_minmax(0,1fr)]">
              <div
                className={cn(
                  'overflow-y-auto bg-background lg:block lg:border-r',
                  mobileDetailOpen ? 'hidden' : 'block',
                )}
              >
                <div className="flex items-center gap-2 border-b p-3">
                  <label className="relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder="Search templates…"
                      className="h-9 w-full rounded-md border bg-background pl-9 pr-3 text-sm outline-none focus:border-foreground"
                    />
                  </label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0">
                        <Filter className="h-4 w-4" />
                        <span className="sr-only">Filter templates</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <DropdownMenuItem onSelect={() => setStatusFilter('all')}>
                        All statuses
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setStatusFilter('active')}>
                        Active only
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => setStatusFilter('inactive')}>
                        Archived only
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {PRIORITY_OPTIONS.filter((option) => option.value !== 'all').map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={() => setPriorityFilter(option.value)}
                        >
                          {option.label} priority
                        </DropdownMenuItem>
                      ))}
                      {activeFilterCount > 0 ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={resetFilters}>Clear filters</DropdownMenuItem>
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {groupedJobTypes.map(([group, groupJobTypes]) => (
                  <section key={group}>
                    <div className="flex items-center justify-between border-b bg-muted/30 px-4 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                        {group}
                      </p>
                      <span className="text-[10px] text-muted-foreground">
                        {groupJobTypes.length}
                      </span>
                    </div>
                    <div className="divide-y">
                      {groupJobTypes.map((jobType) => {
                        const summary = jobTypeLineItemSummary(jobType);
                        const isSelected = selectedJobType?.id === jobType.id;
                        return (
                          <button
                            key={jobType.id}
                            type="button"
                            className={cn(
                              'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                              isSelected && 'bg-muted/60',
                            )}
                            onClick={() => openDetail(jobType.id)}
                          >
                            <JobTypeAppearanceTile
                              color={jobType.color}
                              icon={jobType.icon}
                              size="lg"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold">
                                {jobType.name}
                              </span>
                              <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                                {jobType.shortCode} · {formatDuration(jobType.defaultDuration)} ·{' '}
                                {summary.count > 0 ? formatPrice(summary.total) : 'No price'} ·{' '}
                                {jobType.minCrewSize} tech
                                {jobType.minCrewSize === 1 ? '' : 's'}
                              </span>
                            </span>
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                              {jobType.jobCount} {jobType.jobCount === 1 ? 'job' : 'jobs'}
                            </span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
                <div className="flex items-center justify-between border-t px-4 py-3 text-xs text-muted-foreground">
                  <span>{sortedJobTypes.length} templates</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 px-2 text-xs font-normal"
                      >
                        {sortKey === 'updatedAt'
                          ? 'Recently updated'
                          : sortDir === 'desc'
                            ? 'Name Z–A'
                            : 'Name A–Z'}
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          setSortKey('jobType');
                          setSortDir('asc');
                        }}
                      >
                        Name A–Z
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          setSortKey('jobType');
                          setSortDir('desc');
                        }}
                      >
                        Name Z–A
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => {
                          setSortKey('updatedAt');
                          setSortDir('desc');
                        }}
                      >
                        Recently updated
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div
                className={cn(
                  'min-h-0 overflow-y-auto bg-background lg:block',
                  mobileDetailOpen ? 'block' : 'hidden',
                )}
              >
                <div className="border-b px-3 py-2 lg:hidden">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="-ml-2 gap-1.5"
                    onClick={() => setMobileDetailOpen(false)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    All job types
                  </Button>
                </div>
                {selectedJobType ? (
                  <JobTypeTemplateDetail
                    jobType={selectedJobType}
                    onEdit={() => openEditDrawer(selectedJobType.id)}
                    onDuplicate={() => void handleDuplicate(selectedJobType)}
                    isDuplicating={duplicatingJobTypeId === selectedJobType.id}
                    onArchive={() =>
                      archiveJobType.mutate({
                        id: selectedJobType.id,
                        isActive: !selectedJobType.isActive,
                      })
                    }
                    onDelete={() => setDeleteTarget(selectedJobType)}
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <SettingsDataGrid
              className="m-4 min-h-0 flex-1 overflow-hidden rounded-xl border bg-background"
              columns={JOB_TYPE_COLUMNS}
              rows={tableRows}
              getRowKey={(row) => row.id}
              storageKey="settings-job-types-v2"
              emptyIcon={Wrench}
              emptyTitle="No matching job types found"
              searchValue={search}
              onSearchChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              searchPlaceholder="Search job types..."
              showClearAll={activeFilterCount > 0}
              onClearAll={resetFilters}
              activeFilterCount={activeFilterCount}
              renderCell={(key, row) =>
                row.kind === 'jobType' ? renderCell(key, row.jobType) : null
              }
              renderRowActions={(row) =>
                row.kind === 'jobType' ? (
                  <JobTypeRowActions
                    jobType={row.jobType}
                    onEdit={() => openEditDrawer(row.jobType.id)}
                    onArchive={() =>
                      archiveJobType.mutate({
                        id: row.jobType.id,
                        isActive: !row.jobType.isActive,
                      })
                    }
                    onDelete={() => setDeleteTarget(row.jobType)}
                  />
                ) : null
              }
              onRowClick={(row) => {
                if (row.kind !== 'jobType') return;
                setSelectedJobTypeId(row.jobType.id);
                setExpandedTableJobTypeId((current) =>
                  current === row.jobType.id ? null : row.jobType.id,
                );
              }}
              isFullWidthRow={(row) => row.kind === 'detail'}
              getRowHeight={(row) => (row.kind === 'detail' ? 176 : 56)}
              renderFullWidthRow={(row) =>
                row.kind === 'detail' ? (
                  <JobTypeTableExpansion
                    jobType={row.jobType}
                    checklistDefinitions={assignableFormDefinitions}
                    onOpen={() => {
                      setSelectedJobTypeId(row.jobType.id);
                      setViewMode('library');
                    }}
                    onEdit={() => openEditDrawer(row.jobType.id)}
                  />
                ) : null
              }
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSort}
              pagination={{
                count: sortedJobTypes.length,
                singular: 'job type',
                plural: 'job types',
                page: currentPage,
                totalPages,
                pageSize: PAGE_SIZE,
                onPrevious: () => setPage((current) => Math.max(1, current - 1)),
                onNext: () => setPage((current) => Math.min(totalPages, current + 1)),
              }}
            />
          )}
        </div>
      </DetailPageLayout>

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete job type?</DialogTitle>
            <DialogDescription>
              {deleteTarget ? (
                deleteTarget.jobCount > 0 ? (
                  <>
                    <strong>{deleteTarget.name}</strong> is used by {deleteTarget.jobCount}{' '}
                    {deleteTarget.jobCount === 1 ? 'job' : 'jobs'} and cannot be deleted. Archive it
                    to prevent new jobs from using it.
                  </>
                ) : (
                  <>
                    Delete <strong>{deleteTarget.name}</strong> permanently? If it is referenced by
                    booking services or checklists, deletion will be rejected and it should be
                    archived instead.
                  </>
                )
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            {deleteTarget ? (
              <Button
                variant="outline"
                disabled={deleteJobType.isPending || archiveJobType.isPending}
                onClick={() => {
                  archiveJobType.mutate(
                    { id: deleteTarget.id, isActive: false },
                    { onSuccess: () => setDeleteTarget(null) },
                  );
                }}
              >
                <Archive className="h-3.5 w-3.5" />
                Archive instead
              </Button>
            ) : null}
            <Button
              variant="outline"
              disabled={deleteJobType.isPending || archiveJobType.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                deleteJobType.isPending ||
                archiveJobType.isPending ||
                Boolean(deleteTarget && deleteTarget.jobCount > 0)
              }
              onClick={() => {
                if (!deleteTarget || deleteTarget.jobCount > 0) return;
                deleteJobType.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                });
              }}
            >
              {deleteJobType.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function JobTypeTemplateDetail({
  jobType,
  onEdit,
  onDuplicate,
  isDuplicating = false,
  onArchive,
  onDelete,
}: {
  jobType: JobType;
  onEdit: () => void;
  onDuplicate: () => void;
  isDuplicating?: boolean;
  onArchive?: () => void;
  onDelete?: () => void;
}) {
  const { count: lineItemCount } = jobTypeLineItemSummary(jobType);
  const lineItems = selectedLineItems(jobType);
  // The auto-managed general skill is how "no specific skill" is stored, not a
  // requirement anyone chose — read it as the empty state it stands for.
  const requiredSkills = specificRequiredSkills(jobType.requiredSkills);
  const { data: pipelines = [] } = useJobPipelines();
  const { data: businessUnits = [] } = useBusinessUnits('all');
  // Job-scope tag catalog, to resolve the stamped tagIds into names.
  const { data: jobScopeTags = [] } = useTags('job');
  const stampedTags = (jobType.tagIds ?? [])
    .map((tagId) => jobScopeTags.find(({ id }) => id === tagId))
    .filter((tag): tag is (typeof jobScopeTags)[number] => !!tag);
  const pipeline = pipelines.find(({ id }) => id === jobType.pipelineId);
  const jobTypeBusinessUnits = (jobType.businessUnitIds ?? [])
    .map((unitId) => businessUnits.find(({ id }) => id === unitId))
    .filter((unit): unit is (typeof businessUnits)[number] => !!unit);
  const initialStage = pipeline?.stages.find(({ id }) => id === jobType.defaultStageId);
  const authorizationRequired = jobType.authorizationConfig?.required === true;
  const authorizationSummary = authorizationRequired
    ? [
        formatLabel(jobType.authorizationConfig?.method ?? 'Required'),
        jobType.authorizationConfig?.maxAmount != null
          ? `up to ${formatPrice(jobType.authorizationConfig.maxAmount)}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Not required';
  const cancellationFee =
    jobType.cancellationFeeType === 'FLAT'
      ? `${formatPrice(jobType.cancellationFeeAmount ?? 0)} fee`
      : jobType.cancellationFeeType === 'PERCENT'
        ? `${jobType.cancellationFeeAmount ?? 0}% fee`
        : 'No fee';
  const cancellationSummary = [
    cancellationFee,
    jobType.cancellationWindowHours != null
      ? `${jobType.cancellationWindowHours}h free cancellation`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const schedulingAvailability = resolveJobTypeBookingType(jobType);
  const supportsOneOff = schedulingAvailability === 'ONE_OFF';
  const supportsRecurring = schedulingAvailability === 'RECURRING';
  const { total: visitTotal } = jobTypeLineItemSummary(jobType);
  const isDiagnostic =
    jobType.scopeMode === 'DIAGNOSTIC' ||
    jobType.serviceCallEnabled === true ||
    jobType.irisMode === 'SERVICE_CALL' ||
    jobType.isServiceCallFallback;
  const irisActivated = readIrisListingFields(jobType).irisActivated;
  const surveyQuestionCount = readIrisSurveyQuestions(jobType.irisSurveyQuestions).length;
  const creditableLineCount = lineItems.filter(
    (item) => (item as Record<string, unknown>).isServiceCallFee !== false,
  ).length;

  return (
    <article>
      <header className="flex items-start justify-between gap-5 border-b bg-background px-5 py-4">
        <div className="flex min-w-0 items-start gap-4">
          <JobTypeAppearanceTile color={jobType.color} icon={jobType.icon} size="xl" />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="truncate text-xl font-semibold tracking-tight">{jobType.name}</h2>
              <Badge variant="outline" className="font-mono text-xs">
                {jobType.shortCode}
              </Badge>
              <StatusChip
                status={jobType.isActive ? 'ACTIVE' : 'INACTIVE'}
                label={statusLabel(jobType)}
                variant="table"
              />
            </div>
            {jobType.description ? (
              <p className="mt-1 max-w-3xl text-xs leading-5 text-muted-foreground">
                {jobType.description}
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={onDuplicate} disabled={isDuplicating}>
            <Copy className="h-4 w-4" />
            {isDuplicating ? 'Duplicating...' : 'Duplicate'}
          </Button>
          <Button size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          {onArchive && onDelete ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9" aria-label="More actions">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={onArchive}>
                  {jobType.isActive ? (
                    <Archive className="h-4 w-4" />
                  ) : (
                    <ArchiveRestore className="h-4 w-4" />
                  )}
                  {jobType.isActive ? 'Archive' : 'Restore'}
                </DropdownMenuItem>
                {jobType.jobCount === 0 ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </header>

      <div className="space-y-4 p-5">
        <section className="grid overflow-hidden rounded-xl border bg-background lg:grid-cols-2 lg:divide-x">
          <div>
            <JobTypeDetailSectionHeader
              title="Pricebook lines"
              meta={`${lineItemCount} selected`}
              icon={ReceiptText}
            />
            {lineItems.length > 0 ? (
              <div className="divide-y">
                {lineItems.slice(0, 5).map((item, index) => (
                  <div
                    key={String(item.lineItemId ?? item._rowId ?? `${lineItemName(item)}-${index}`)}
                    className="grid h-12 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-5"
                  >
                    <span className="truncate text-xs font-medium">{lineItemName(item)}</span>
                    <span className="text-[10px] text-muted-foreground">
                      ×{lineItemQuantity(item)}
                    </span>
                    <span className="min-w-16 text-right text-xs font-medium tabular-nums">
                      {formatPrice(lineItemTotal(item))}
                    </span>
                  </div>
                ))}
                {lineItems.length > 5 ? (
                  <p className="flex h-12 items-center px-5 text-xs text-muted-foreground">
                    +{lineItems.length - 5} more lines
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="flex h-12 items-center px-5 text-xs text-muted-foreground">
                No pricebook lines selected.
              </p>
            )}
            <div className="border-t">
              <JobTypeSettingRow
                label="Booking deposit"
                value={visitDepositLabel(jobType)}
                detail={
                  jobType.visitDepositEnabled
                    ? 'Iris / online booking send a payment link at booking'
                    : undefined
                }
              />
            </div>
          </div>
          <div>
            <JobTypeDetailSectionHeader
              title="Who can take it"
              meta={`${requiredSkills.length} skill${requiredSkills.length === 1 ? '' : 's'}`}
              icon={Wrench}
            />
            {requiredSkills.length > 0 ? (
              <div className="divide-y">
                {requiredSkills.map((skill) => (
                  <div key={skill.id} className="flex h-12 items-center justify-between gap-3 px-5">
                    <span className="flex min-w-0 items-center gap-2 text-xs font-medium">
                      <span
                        className={cn(
                          'h-2 w-2 shrink-0 rounded-full',
                          tradeDotClass(skill.trade?.color ?? skill.color),
                        )}
                      />
                      <span className="truncate">{skill.name}</span>
                    </span>
                    <Badge variant="outline" className="text-[10px]">
                      Required
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="flex h-12 items-center px-5 text-xs text-muted-foreground">
                Any active technician can qualify.
              </p>
            )}
          </div>
        </section>

        <section
          className={cn(
            'grid overflow-hidden rounded-xl border bg-background',
            supportsOneOff && supportsRecurring
              ? 'xl:grid-cols-3 xl:divide-x'
              : 'lg:grid-cols-2 lg:divide-x',
          )}
        >
          <div>
            <JobTypeDetailSectionHeader title="Shared defaults" icon={Settings} />
            <div className="divide-y">
              <JobTypeSettingRow
                label="Crew"
                value={`${jobType.minCrewSize} tech${jobType.minCrewSize === 1 ? '' : 's'}`}
                detail={
                  requiredSkills.length > 0
                    ? `${requiredSkills.length} skill${requiredSkills.length === 1 ? '' : 's'} required`
                    : 'No skill restrictions'
                }
              />
              <JobTypeSettingRow label="Priority" value={formatLabel(jobType.defaultPriority)} />
              <JobTypeSettingRow
                label={jobTypeBusinessUnits.length > 1 ? 'Business units' : 'Business unit'}
                value={
                  jobTypeBusinessUnits.length > 0
                    ? jobTypeBusinessUnits.map((unit) => unit.name).join(', ')
                    : 'Unassigned'
                }
              />
              <JobTypeSettingRow
                label="Job tags"
                value={
                  stampedTags.length > 0 ? stampedTags.map((tag) => tag.name).join(', ') : 'None'
                }
                detail={stampedTags.length > 0 ? 'Stamped on every job of this type' : undefined}
              />
              <JobTypeSettingRow label="Authorization" value={authorizationSummary} />
              <JobTypeSettingRow
                label="Auto-charge"
                value={booleanJobDefaultLabel(jobType.autoCharge)}
              />
              <JobTypeSettingRow
                label="Visit cancellation"
                value={cancellationSummary || 'No fee'}
              />
              <JobTypeSettingRow
                label="Refund deposit on cancel"
                value={jobType.depositRefundable === false ? 'No' : 'Yes'}
              />
            </div>
          </div>
          <div>
            <JobTypeDetailSectionHeader title="Notifications" icon={Bell} />
            <div className="divide-y">
              <JobTypeSettingRow
                label="Scheduled job creation"
                value={schedulingNotificationPolicyLabel(jobType.schedulingNotificationPolicy)}
              />
            </div>
          </div>
          {supportsOneOff ? (
            <div>
              <JobTypeDetailSectionHeader title="One-off defaults" icon={Clock} />
              <div className="divide-y">
                <JobTypeSettingRow label="Pipeline" value={pipeline?.name ?? 'No pipeline'} />
                <JobTypeSettingRow
                  label="Initial stage"
                  value={initialStage?.name ?? 'First stage'}
                />

                <JobTypeSettingRow
                  label="Auto-invoice"
                  value={booleanJobDefaultLabel(jobType.autoInvoice)}
                  detail="Fixed-price jobs only"
                />
              </div>
            </div>
          ) : null}
          {supportsRecurring ? (
            <div>
              <JobTypeDetailSectionHeader title="Recurring defaults" icon={ShieldCheck} />
              <div className="divide-y">
                <JobTypeSettingRow label="Cadence" value={recurrenceLabel(jobType)} />
                <JobTypeSettingRow
                  label="Weather policy"
                  value={weatherPolicyLabel(jobType.weatherPolicy)}
                  detail={
                    jobType.weatherPolicy == null
                      ? 'Subscription skips; other billing makes up'
                      : undefined
                  }
                />
                <JobTypeSettingRow
                  label="Invoice amount"
                  value={invoicingMethodLabel(jobType.invoicingMethod)}
                />
                <JobTypeSettingRow
                  label="Invoice timing"
                  value={invoicingScheduleLabel(jobType.invoicingSchedule)}
                  detail="Subscription and prepaid use their own schedule"
                />
              </div>
            </div>
          ) : null}
        </section>

        <section className="grid overflow-hidden rounded-xl border bg-background lg:grid-cols-2 lg:divide-x">
          <div>
            <JobTypeDetailSectionHeader
              title="Diagnostic workflow"
              meta={isDiagnostic ? 'Enabled' : 'Off'}
              icon={ClipboardList}
            />
            {isDiagnostic ? (
              <div className="divide-y">
                <JobTypeSettingRow
                  label="Visit price"
                  value={visitTotal > 0 ? formatPrice(visitTotal) : 'Free visit'}
                  detail="Sum of the selected pricebook lines"
                />
                <JobTypeSettingRow
                  label="Visit-price credit"
                  value={serviceCallFeePolicyLabel(jobType.serviceCallFeePolicy)}
                  detail={
                    creditableLineCount < lineItemCount
                      ? `${creditableLineCount} of ${lineItemCount} lines credit toward work`
                      : undefined
                  }
                />
              </div>
            ) : (
              <p className="flex h-12 items-center px-5 text-xs text-muted-foreground">
                Jobs of this type are regular jobs, not diagnostic visits.
              </p>
            )}
          </div>
          <div>
            <JobTypeDetailSectionHeader
              title="Iris"
              meta={irisActivated ? 'Listed' : 'Not listed'}
              icon={Bot}
            />
            <div className="divide-y">
              <JobTypeSettingRow
                label="Iris"
                value={irisActivated ? 'Listing this job type' : 'Will not list this job type'}
              />
              {irisActivated ? (
                <>
                  <JobTypeSettingRow
                    label="Customer phrases"
                    value={
                      (jobType.customerPhrases?.length ?? 0) > 0
                        ? `${jobType.customerPhrases.length} phrase${jobType.customerPhrases.length === 1 ? '' : 's'}`
                        : 'None'
                    }
                  />
                  <JobTypeSettingRow
                    label="Intake survey"
                    value={
                      surveyQuestionCount > 0
                        ? `${surveyQuestionCount} question${surveyQuestionCount === 1 ? '' : 's'}`
                        : 'No questions'
                    }
                  />
                  <JobTypeSettingRow
                    label="Equipment awareness"
                    value={jobType.irisAskEquipment ? 'On' : 'Off'}
                    detail={
                      jobType.irisAskEquipment
                        ? 'Iris asks which unit the visit concerns'
                        : undefined
                    }
                  />
                </>
              ) : null}
              <JobTypeSettingRow
                label="Terms & conditions"
                value={termsModeLabel(jobType.termsMode)}
              />
            </div>
          </div>
        </section>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t px-1 pt-4 text-xs text-muted-foreground">
          <span>
            Used in {jobType.jobCount} {jobType.jobCount === 1 ? 'job' : 'jobs'}
            {' · '}
            Updated {formatDate(jobType.updatedAt)}
          </span>
          <span>Existing jobs keep the values they were created with.</span>
        </footer>
      </div>
    </article>
  );
}

function visitDepositLabel(jobType: JobType) {
  if (!jobType.visitDepositEnabled) return 'Not required';
  const value = Number(jobType.visitDepositValue ?? 0);
  if (!(value > 0)) return 'Not required';
  if (jobType.visitDepositType === 'PERCENT') {
    const { total } = jobTypeLineItemSummary(jobType);
    const approx = total > 0 ? ` (≈${formatPrice((total * value) / 100)})` : '';
    return `${value}% of visit price${approx} upfront`;
  }
  return `${formatPrice(value)} upfront`;
}

function serviceCallFeePolicyLabel(value: string | null | undefined) {
  switch (value) {
    case 'NONE':
      return 'Always charged';
    case 'CREDIT_ON_ESTIMATE':
      return 'Credited on the first estimate';
    case 'CREDIT_ON_CHILD_JOB':
      return 'Credited on the follow-up job';
    default:
      return 'Credited toward work when the customer proceeds';
  }
}

function termsModeLabel(value: string | null | undefined) {
  if (value === 'CUSTOM') return 'Custom text';
  if (value === 'TEMPLATE') return 'Pinned template';
  return 'Company default';
}

function booleanJobDefaultLabel(value: boolean | null | undefined) {
  if (value == null) return 'Off (job default)';
  return value ? 'On' : 'Off';
}

function schedulingNotificationPolicyLabel(value: JobType['schedulingNotificationPolicy']) {
  return {
    ASK: 'Ask on job creation',
    NONE: "Don't send notifications",
    SMS: 'Send SMS',
    EMAIL: 'Send email',
    BOTH: 'Send SMS and email',
  }[value ?? 'ASK'];
}

function weatherPolicyLabel(value: JobType['weatherPolicy']) {
  if (value == null) return 'Automatic';
  return value === 'SKIP' ? 'Skip interrupted visits' : 'Make up interrupted visits';
}

function invoicingMethodLabel(value: JobType['invoicingMethod']) {
  if (value == null) return 'From job pricing';
  return value === 'FIXED_PRICE' ? 'Fixed job total' : 'Completed visit charges';
}

function invoicingScheduleLabel(value: JobType['invoicingSchedule']) {
  if (value == null || value === 'ON_COMPLETION') return 'After each completed visit';
  if (value === 'LAST_DAY_OF_MONTH') return 'Monthly, on the last day';
  if (value === 'CUSTOM_SCHEDULE') return 'Legacy custom schedule';
  return 'Legacy as-needed schedule';
}

function JobTypeSettingRow({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 px-5 py-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right">
        <strong className="block truncate text-xs font-medium">{value}</strong>
        {detail ? <span className="block text-[10px] text-muted-foreground">{detail}</span> : null}
      </span>
    </div>
  );
}

function JobTypeDetailSectionHeader({
  title,
  meta,
  icon: Icon,
}: {
  title: string;
  meta?: string;
  icon: typeof Clock;
}) {
  return (
    <div className="flex items-center justify-between border-b px-5 py-3">
      <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </h3>
      {meta ? <span className="text-[10px] text-muted-foreground">{meta}</span> : null}
    </div>
  );
}

function JobTypeTableExpansion({
  jobType,
  checklistDefinitions,
  onOpen,
  onEdit,
}: {
  jobType: JobType;
  checklistDefinitions: JobTypeChecklistDefinition[];
  onOpen: () => void;
  onEdit: () => void;
}) {
  const lineItems = selectedLineItems(jobType);
  const checklistLinks = checklistLinkSummaryForJobType(jobType.id, checklistDefinitions);
  const blockingCount = checklistLinks.filter(({ link }) => link.blocksCompletion).length;
  const requiredSkills = specificRequiredSkills(jobType.requiredSkills);
  const visibleSkills = requiredSkills.slice(0, 3);

  return (
    <div className="grid h-full grid-cols-[minmax(270px,1.35fr)_minmax(180px,0.75fr)_minmax(220px,1fr)_150px] items-start gap-4 border-y bg-background px-5 py-4 text-xs">
      <section>
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Pick this and you get
        </h3>
        <div className="mt-3 grid grid-cols-[90px_minmax(0,1fr)] items-center gap-x-3 gap-y-2">
          <span className="text-muted-foreground">Time block</span>
          <strong>{formatDuration(jobType.defaultDuration)} job</strong>
          <span className="text-muted-foreground">Crew</span>
          <span className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              <strong className="text-foreground">
                {jobType.minCrewSize} tech{jobType.minCrewSize === 1 ? '' : 's'}
              </strong>
              {requiredSkills.length > 0
                ? `all ${requiredSkills.length} skills required`
                : 'no skill restrictions'}
            </span>
          </span>
        </div>
      </section>

      <section>
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Pricebook lines
        </h3>
        <div className="mt-2 space-y-1.5">
          {lineItems.slice(0, 2).map((item, index) => (
            <div
              key={String(item.lineItemId ?? item._rowId ?? `${lineItemName(item)}-${index}`)}
              className="grid grid-cols-[minmax(0,1fr)_auto] gap-3"
            >
              <span className="truncate">{lineItemName(item)}</span>
              <strong className="tabular-nums">{formatPrice(lineItemTotal(item))}</strong>
            </div>
          ))}
          {lineItems.length > 2 ? (
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
              <span className="text-muted-foreground">+{lineItems.length - 2} more lines</span>
              <strong className="tabular-nums">
                {formatPrice(
                  lineItems.slice(2).reduce((sum, item) => sum + lineItemTotal(item), 0),
                )}
              </strong>
            </div>
          ) : null}
          {lineItems.length === 0 ? (
            <span className="text-muted-foreground">No pricebook lines</span>
          ) : null}
        </div>
      </section>

      <section>
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Requirements
        </h3>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {visibleSkills.map((skill) => (
            <Badge key={skill.id} variant="outline" className="gap-1 text-[10px]">
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  tradeDotClass(skill.trade?.color ?? skill.color),
                )}
              />
              {skill.name}
            </Badge>
          ))}
          {requiredSkills.length > visibleSkills.length ? (
            <Badge variant="outline" className="text-[10px]">
              +{requiredSkills.length - visibleSkills.length}
            </Badge>
          ) : null}
          {requiredSkills.length === 0 ? (
            <span className="text-muted-foreground">No skill requirements</span>
          ) : null}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {blockingCount > 0 ? (
            <Badge
              variant="outline"
              className="border-orange-200 bg-orange-50 text-[10px] text-orange-700"
            >
              <ClipboardList className="mr-1 h-3 w-3" />
              {blockingCount} blocking checklist{blockingCount === 1 ? '' : 's'}
            </Badge>
          ) : null}
          {jobType.signoffConfig?.required ? (
            <Badge variant="outline" className="text-[10px]">
              <FileSignature className="mr-1 h-3 w-3" />
              Signature
            </Badge>
          ) : null}
        </div>
      </section>

      <section className="flex min-w-0 flex-col items-stretch text-right">
        <p className="text-[10px] text-muted-foreground">
          {jobType.jobCount} {jobType.jobCount === 1 ? 'job' : 'jobs'}
        </p>
        <Button size="sm" className="mt-2 w-full px-2" onClick={onOpen}>
          Open template
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
        <Button variant="outline" size="sm" className="mt-2 w-full px-2" onClick={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      </section>
    </div>
  );
}

function JobTypeRowActions({
  jobType,
  onEdit,
  onArchive,
  onDelete,
}: {
  jobType: JobType;
  onEdit: () => void;
  onArchive: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onArchive}>
          {jobType.isActive ? (
            <Archive className="h-3.5 w-3.5" />
          ) : (
            <ArchiveRestore className="h-3.5 w-3.5" />
          )}
          {jobType.isActive ? 'Archive' : 'Restore'}
        </DropdownMenuItem>
        {jobType.jobCount === 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function statusLabel(jobType: JobType) {
  return jobType.isActive ? 'Active' : 'Inactive';
}
